import React, { useEffect } from "react";
import { Card, Table, Tag } from "antd";
import { FormattedMessage } from "react-intl";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import styled from "styled-components";

import MQTTService from "../../services/mqtt";

import themeColors from "../../lib/style/theme";
import { MESSAGE_TYPE } from "../../lib/utils/constants";

import { selectors as NODE_SELECTORS, actions as NODE_ACTIONS } from "../../ducks/node";

import NodeAnalyticsRemoveResults from "./NodeAnalyticsRemoveResults";

const NodeAnalyticsResults = () => {
  const { cwid, cnn } = useParams();
  const dispatch = useDispatch();
  const testResults = useSelector(NODE_SELECTORS.getNodeTestResults);
  const [searchParams] = useSearchParams();
  const channelId = searchParams.get("c");

  useEffect(() => {
    return () => {
      dispatch(NODE_ACTIONS.SET_NODE_TEST_RESULTS(null));
    };
  }, []);

  useEffect(() => {
    if (channelId) {
      MQTTService.sendMessageV5({
        topic: `node/${cwid}/in`,
        message: {
          msgType: MESSAGE_TYPE.GET_LAST_TEST_RESULTS,
          channelId,
        },
      });
    }
  }, [channelId]);

  const columns = [
    {
      title: <FormattedMessage id="NodeAnalytics.time" defaultMessage="Time" />,
      dataIndex: "timestamp",
      key: "timestamp",
      width: "180px",
      render: (timestamp) => {
        return moment.unix(timestamp).format("YYYY-MM-DD HH:mm:ss");
      },
      sorter: (currentValue, nextValue) => currentValue.timestamp - nextValue.timestamp,
    },
    {
      title: <FormattedMessage id="NodeAnalytics.channel" defaultMessage="Channel" />,
      dataIndex: "channelName",
      key: "channelName",
      width: "180px",
      sorter: (currentValue, nextValue) => currentValue.channelName.localeCompare(nextValue.channelName),
      render: (channelName, record) => {
        const linkUrl = `/node/${cwid}/${cnn}/channel/${record?.channelId}`;

        if (record?.channelId <= 0) {
          return null;
        }

        return <Link to={linkUrl}>{channelName}</Link>;
      },
    },
    {
      title: <FormattedMessage id="NodeAnalytics.output" defaultMessage="Output" />,
      dataIndex: "params",
      key: "outputName",
      width: "180px",
      render: ({ outputName }) => {
        return <span>{outputName}</span>;
      },
      sorter: (currentValue, nextValue) =>
        currentValue?.params?.outputName?.localeCompare(nextValue?.params?.outputName),
    },
    {
      title: <FormattedMessage id="NodeAnalytics.type" defaultMessage="Type" />,
      dataIndex: "testType",
      key: "testType",
      align: "center",
      width: "120px",
      sorter: (currentValue, nextValue) => currentValue.testType.localeCompare(nextValue.testType),
      render: (testType) => {
        return <Tag>{testType}</Tag>;
      },
    },
    {
      title: <FormattedMessage id="NodeAnalytics.model" defaultMessage="Model" />,
      dataIndex: "params",
      key: "modelName",
      align: "center",
      width: "120px",
      sorter: (currentValue, nextValue) => currentValue?.params?.modelName?.localeCompare(nextValue?.params?.modelName),
      render: ({ modelName }) => {
        return <Tag>{modelName}</Tag>;
      },
    },
    {
      title: <FormattedMessage id="NodeAnalytics.duration" defaultMessage="Duration" />,
      dataIndex: "duration",
      key: "duration",
      width: "80px",
      align: "center",
      render: (duration) => {
        return <span>{duration} [s]</span>;
      },
      sorter: (currentValue, nextValue) => currentValue.duration - nextValue.duration,
    },
    {
      title: <FormattedMessage id="NodeAnalytics.score" defaultMessage="Score" />,
      dataIndex: "score",
      key: "score",
      render: (score) => {
        return <StyledSpan>{Number(score).toFixed(3)}</StyledSpan>;
      },
      sorter: (currentValue, nextValue) => currentValue.score - nextValue.score,
    },
  ];

  return (
    <Card
      title={<FormattedMessage id="NodeAnalyticsResults.results" defaultMessage="Results" />}
      extra={<NodeAnalyticsRemoveResults />}
    >
      <Table
        dataSource={testResults}
        columns={columns}
        rowKey={(record) => `${record.timestamp}-${record.channelId}`}
        pagination={{ pageSize: 20 }}
      />
    </Card>
  );
};

const StyledSpan = styled.span`
  color: ${themeColors.maritime};
`;

export default NodeAnalyticsResults;
